import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';
import './tailwind.css';
import { FaEdit, FaTrash, FaKey, FaCheck, FaTimes, FaSpinner } from 'react-icons/fa';

export default function GestisciAgenti() {
  /* ----------------------------- STATE ---------------------------------- */
  const [agentiList, setAgentiList] = useState([]);
  const [agenzieOptions, setAgenzieOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const navigate = useNavigate();

  // modal di conferma / input password
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [onConfirmAction, setOnConfirmAction] = useState(null);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [tempPassword, setTempPassword] = useState('');

  // inline edit
  const [editingId, setEditingId] = useState(null);
  const [editedData, setEditedData] = useState({});

  const SUPABASE_FUNCTIONS_BASE_URL =
    'https://zwgpmobugscpdcavajbu.supabase.co';

  /* ---------------------- MODAL HELPERS ---------------------------------- */
  const openConfirmModal = useCallback(
    (title, message, confirmCallback, showPassword = false) => {
      setModalTitle(title);
      setModalMessage(message);
      setOnConfirmAction(() => confirmCallback); // salvo il callback
      setShowPasswordInput(showPassword);
      setTempPassword('');
      setShowConfirmModal(true);
    },
    []
  );

  const closeConfirmModal = useCallback(() => {
    setShowConfirmModal(false);
    setModalTitle('');
    setModalMessage('');
    setOnConfirmAction(null);
    setShowPasswordInput(false);
    setTempPassword('');
  }, []);

  const handleConfirm = () => {
    if (onConfirmAction) {
      // passa SEMPRE la password aggiornata (anche se non serve)
      onConfirmAction(tempPassword);
    }
  };

  /* -------------------- CARICA UTENTI + AGENZIE -------------------------- */
  const loadAgenti = useCallback(async () => {
    setLoading(true);
    try {
      // 1. utenti role = agente
      const { data: usersData, error: usersError } = await supabase
        .from('utenti')
        .select('id, nome, cognome, email, agenzia_id')
        .eq('ruolo', 'agente');
      if (usersError) throw usersError;

      // 2. agenzie (x dropdown e nome)
      const { data: agenzieData, error: agenzieError } = await supabase
        .from('agenzie')
        .select('id, nome');
      if (agenzieError) throw agenzieError;

      const agenzieMap = agenzieData.reduce((acc, a) => {
        acc[a.id] = a;
        return acc;
      }, {});
      setAgenzieOptions(agenzieData);

      // 3. combinazione
      const combined = usersData.map((u) => ({
        userId: u.id,
        nomeUtente: u.nome,
        cognomeUtente: u.cognome,
        emailUtente: u.email,
        agenziaId: u.agenzia_id,
        nomeAgenzia: agenzieMap[u.agenzia_id]?.nome || 'N/D',
      }));
      setAgentiList(combined);
    } catch (err) {
      console.error('Errore loadAgenti:', err.message);
      openConfirmModal('Errore', `Errore caricamento Account: ${err.message}`, null);
      setAgentiList([]);
    } finally {
      setLoading(false);
    }
  }, [openConfirmModal]);

  /* ---------------------------- EFFECT LOGIN ----------------------------- */
  useEffect(() => {
    const fetchUserRole = async () => {
      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (sessionError || !userId) {
        navigate('/');
        return;
      }
      const { data: profile, error: profileError } = await supabase
        .from('utenti')
        .select('ruolo')
        .eq('id', userId)
        .single();
      if (profileError || !profile || profile.ruolo !== 'broker') {
        navigate('/dashboard/broker');
        return;
      }
      setUserRole(profile.ruolo);
      loadAgenti();
    };

    fetchUserRole();

    const { data: authListener } = supabase.auth.onAuthStateChange((_e, s) => {
      if (!s) navigate('/');
    });
    return () => authListener?.data?.unsubscribe();
  }, [navigate, loadAgenti]);

  /* ------------------------- CRUD HANDLERS ------------------------------- */
  // EDIT
  const handleEdit = (u) => {
    setEditingId(u.userId);
    setEditedData({ ...u });
  };
  const handleCancel = () => {
    setEditingId(null);
    setEditedData({});
  };
  const handleChangeEdit = (e) => {
    const { name, value } = e.target;
    setEditedData((prev) => ({ ...prev, [name]: value }));
  };
  const handleSave = async () => {
    setLoading(true);
    try {
      const { nomeUtente, cognomeUtente, emailUtente, agenziaId } = editedData;
      if (!nomeUtente || !cognomeUtente || !emailUtente || !agenziaId) {
        openConfirmModal('Errore', 'Tutti i campi sono obbligatori', null);
        setLoading(false);
        return;
      }

      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();
      if (sessionError || !sessionData?.session?.access_token) {
        throw new Error('Sessione scaduta. Riesegui il login.');
      }

      const res = await fetch(
        `${SUPABASE_FUNCTIONS_BASE_URL}/functions/v1/update-user-profile`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionData.session.access_token}`,
          },
          body: JSON.stringify({
            userId: editedData.userId,
            nomeUtente,
            cognomeUtente,
            emailUtente,
            agenziaId,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update fallito');

      setEditingId(null);
      setEditedData({});
      await loadAgenti();
      openConfirmModal('Successo', 'Account aggiornato con successo', null);
    } catch (err) {
      console.error(err.message);
      openConfirmModal('Errore', err.message, null);
    } finally {
      setLoading(false);
    }
  };

  // DELETE
  // DELETE
  const handleDeleteAgent = (userId, email) => {
    openConfirmModal(
      'Conferma eliminazione',
      `Eliminare Account ${email}? Azione irreversibile.`,
      async () => {
        setLoading(true);
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          const accessToken = sessionData?.session?.access_token;
          const adminUserId = sessionData?.session?.user?.id;
          if (!accessToken || !adminUserId) throw new Error('Sessione non valida');

          const res = await fetch(
            `${SUPABASE_FUNCTIONS_BASE_URL}/functions/v1/delete-user`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({ userIdToDelete: userId, adminUserId }),
            }
          );
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || 'Errore eliminazione');

          // --- MODIFIED PART ---
          openConfirmModal('Successo', `Account ${email} eliminato`, null); // Pass null for confirmCallback
          await loadAgenti(); // Reload agents immediately after showing success
          // --- END MODIFIED PART ---
        } catch (err) {
          openConfirmModal('Errore', err.message, null);
        } finally {
          setLoading(false);
        }
      }
    );
  };

  // FORCE PASSWORD RESET ---------------------------------------------
  const handleForcePasswordReset = (userId, userEmail) => {
    openConfirmModal(
      'Forza nuova password',
      `Inserisci la nuova password per l'Account ${userEmail}.`,
      async (pwd) => {
        setLoading(true);
        try {
          const passwordToUse = pwd.trim();
          if (passwordToUse.length < 6) {
            openConfirmModal('Errore', 'Password minima 6 caratteri', null);
            setLoading(false);
            return;
          }

          const { data: sessionData } = await supabase.auth.getSession();
          const accessToken = sessionData?.session?.access_token;
          if (!accessToken) throw new Error('Sessione non valida');

          const res = await fetch(
            `${SUPABASE_FUNCTIONS_BASE_URL}/functions/v1/reset-user-password`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({ targetUserId: userId, newPassword: passwordToUse }),
            }
          );
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || 'Reset fallito');

          openConfirmModal('Successo', 'Password aggiornata correttamente', null);
        } catch (err) {
          openConfirmModal('Errore', err.message, null);
        } finally {
          setLoading(false);
        }
      },
      true
    );
  };

  /* --------------------------- RENDER ------------------------------------ */
  if (userRole === null || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 text-slate-800">
        <FaSpinner className="animate-spin text-4xl text-mioblu mr-3" />
        <p className="text-xl font-semibold">Caricamento Account...</p>
      </div>
    );
  }

  if (userRole !== 'broker') return null;

  return (
    <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
      <h1 className="text-2xl md:text-3xl font-bold uppercase mb-4 text-center text-mioblu">
        Gestione Account
      </h1>

      {/* ---------------- TABella ---------------- */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8 overflow-x-auto">
        <table className="min-w-full table-fixed border-collapse">
          <thead>
            <tr className="bg-gray-100 text-mioblu uppercase text-sm leading-normal">
              <th className="py-3 px-6 text-left w-1/5">Nome</th>
              <th className="py-3 px-6 text-left w-1/5">Cognome</th>
              <th className="py-3 px-6 text-left w-1/5">Email</th>
              <th className="py-3 px-6 text-left w-1/5">Sede</th>
              <th className="py-3 px-6 text-center w-1/5">Azioni</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 text-sm font-light">
            {agentiList.length ? (
              agentiList.map((item) => (
                <tr key={item.userId} className="border-b border-gray-200 hover:bg-gray-50">
                  {editingId === item.userId ? (
                    /* ----------------------- ROW EDIT -------------------- */
                    <>
                      <td className="py-3 px-2">
                        <input
                          type="text"
                          name="nomeUtente"
                          value={editedData.nomeUtente}
                          onChange={handleChangeEdit}
                          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-red-500"
                        />
                      </td>
                      <td className="py-3 px-2">
                        <input
                          type="text"
                          name="cognomeUtente"
                          value={editedData.cognomeUtente}
                          onChange={handleChangeEdit}
                          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-red-500"
                        />
                      </td>
                      <td className="py-3 px-2">
                        <input
                          type="email"
                          name="emailUtente"
                          value={editedData.emailUtente}
                          onChange={handleChangeEdit}
                          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-red-500"
                        />
                      </td>
                      <td className="py-3 px-2">
                        <select
                          name="agenziaId"
                          value={editedData.agenziaId || ''}
                          onChange={handleChangeEdit}
                          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-red-500"
                        >
                          <option value="">Seleziona Sede</option>
                          {agenzieOptions.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.nome}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <div className="flex gap-2 justify-center">
                          <button
                            className="w-9 h-9 flex items-center justify-center bg-green-500 text-white rounded hover:bg-green-600"
                            onClick={handleSave}
                            title="Salva"
                            disabled={loading}
                          >
                            <FaCheck />
                          </button>
                          <button
                            className="w-9 h-9 flex items-center justify-center bg-gray-500 text-white rounded hover:bg-gray-600"
                            onClick={handleCancel}
                            title="Annulla"
                            disabled={loading}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    /* --------------------- ROW VIEW ---------------------- */
                    <>
                      <td className="py-3 px-6 font-semibold">{item.nomeUtente}</td>
                      <td className="py-3 px-6">{item.cognomeUtente}</td>
                      <td className="py-3 px-6">{item.emailUtente}</td>
                      <td className="py-3 px-6">{item.nomeAgenzia}</td>
                      <td className="py-3 px-6 text-center">
                        <div className="flex gap-2 justify-center min-h-12">
                          <button
                            className="w-9 h-9 flex items-center justify-center bg-blue-500 text-white rounded hover:bg-blue-600"
                            title="Modifica"
                            onClick={() => handleEdit(item)}
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="w-9 h-9 flex items-center justify-center bg-red-500 text-white rounded hover:bg-red-600"
                            title="Elimina"
                            onClick={() => handleDeleteAgent(item.userId, item.emailUtente)}
                          >
                            <FaTrash />
                          </button>
                          <button
                            className="w-9 h-9 flex items-center justify-center bg-purple-500 text-white rounded hover:bg-purple-600"
                            title="Nuova password"
                            onClick={() => handleForcePasswordReset(item.userId, item.emailUtente)}
                          >
                            <FaKey />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="py-4 text-center text-gray-500">
                  Nessun Account trovato.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ------------------------ MODAL ----------------------------- */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full text-center">
            <h3 className="text-xl font-bold mb-4 text-gray-800">{modalTitle}</h3>
            <p className="text-gray-700 mb-6">{modalMessage}</p>

            {showPasswordInput && (
              <div className="mb-6">
                <input
                  type="password"
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                  placeholder="Nuova password"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Minimo 6 caratteri</p>
              </div>
            )}

            <div className="flex justify-center gap-4">
              {onConfirmAction && (
                <button
                  className="px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  onClick={handleConfirm}
                  disabled={loading || (showPasswordInput && tempPassword.length < 6)}
                >
                  {loading ? <FaSpinner className="animate-spin" /> : 'Sì'}
                </button>
              )}
              <button
                className="px-6 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                onClick={closeConfirmModal}
                disabled={loading}
              >
                {onConfirmAction ? 'No' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
